import {BaseTileProvider} from './base-tile-provider';
import {MapType} from '../../enums/map-type';
import {sprintf} from 'sprintf-js';
import {MapProvider} from '../../enums/map-provider.enum';
import {SettingsService} from '../../services/settings.service';
import {ThemeId} from "../../../common/enums/theme-id";
import {Logger} from "../../../common/utils/logger";

export class GoogleMapsTileProvider extends BaseTileProvider {
  // var URL_GOOGLE_MAPS = 'https://khms%d.googleapis.com/kh?v=716&lyrs=%s&hl=x-local&z=%d&x=%d&y=%d&scale=%f';  // servers: mt[0123].google... lyrs=[s,h,r,t,y,m]... hl=[language]

  // Styling: https://stackoverflow.com/questions/29692737/customizing-google-map-tile-server-url
  private static URL_GOOGLE_MAPS = 'https://mt{sr}.google.com/vt/?lyrs={l}&hl=x-local&z={z}&x={x}&y={y}&src=api&s=Ga&scale={sc}&apistyle={st}';  // servers: mt[0123].google... lyrs=[s,h,r,t,y,m]... hl=[language]
  // private static URL_GOOGLE_MAPS = 'https://mt%d.google.com/vt/?lyrs=%s&hl=x-local&z={z}&x={x}&y={y}&src=api&s=Ga&scale=%f&apistyle=s.t:2|s.e:l|p.v:off';


  constructor(private settings: SettingsService) {
    super();
  }

  getMapProvider(): string {
    return MapProvider.GOOGLE_MAPS;
  }

  getMaxZoom(): number {
    return 21;
  }

  getUrlTemplate(mapType?: MapType): string {
    let mt = mapType;
    if (!mt) {
      mt = SettingsService.settings.mapType;
    }
    let url = GoogleMapsTileProvider.URL_GOOGLE_MAPS
      .replace('{sc}', this.settings.local.showHDMaps ? '2' : '1');
    switch (mt) {
      case MapType.MAP:
        url = url.replace('{l}', 'm@339000000');
        break;
      case MapType.SATELLITE:
        url = url.replace('{l}', 's@339000000');
        break;
      case MapType.HYBRID:
        url = url.replace('{l}', 's@339000000,h@339000000');
        break;
      case MapType.TERRAIN:
        url = url.replace('{l}', 'p');
        break;
    }

    if (this.settings.local.theme === ThemeId.LIGHT) {
      url = url.replace('{st}', this.getEncodedStyles(this.getLightStyle()));
    } else {
      url = url.replace('{st}', this.getEncodedStyles(this.getDarkStyle()));
    }
    return url;
  }

  getUrl(x, y, zoom, mapType?: MapType): string {
    return this.getUrlTemplate(mapType).replace('{sr}', ''+(x + y) % 4)
      .replace('{z}', '' + zoom)
      .replace('{x}', '' + x)
      .replace('{y}', '' + y);
  }

  getEncodedStyles(styles) {
    let ret = '';
    const styleparseTypes = {
      'all': '0',
      'administrative': '1',
      'administrative.country': '17',
      'administrative.land_parcel': '21',
      'administrative.locality': '19',
      'administrative.neighborhood': '20',
      'administrative.province': '18',
      'landscape': '5',
      'landscape.man_made': '81',
      'landscape.natural': '82',
      'poi': '2',
      'poi.attraction': '37',
      'poi.business': '33',
      'poi.government': '34',
      'poi.medical': '36',
      'poi.park': '40',
      'poi.place_of_worship': '38',
      'poi.school': '35',
      'poi.sports_complex': '39',
      'road': '3',
      'road.arterial': '50',
      'road.highway': '49',
      'road.local': '51',
      'transit': '4',
      'transit.line': '65',
      'transit.station': '66',
      'water': '6'
    };
    const styleparseElements = {
      'all': 'a',
      'geometry': 'g',
      'geometry.fill': 'g.f',
      'geometry.stroke': 'g.s',
      'labels': 'l',
      'labels.icon': 'l.i',
      'labels.text': 'l.t',
      'labels.text.fill': 'l.t.f',
      'labels.text.stroke': 'l.t.s'
    };
    const styleparseStylers = {
      'color': 'p.c',
      'gamma': 'p.g',
      'hue': 'p.h',
      'invert_lightness': 'p.il',
      'lightness': 'p.l',
      'saturation': 'p.s',
      'visibility': 'p.v',
      'weight': 'p.w'
    };
    for (const item of styles) {
      if (item.featureType) {
        ret += 's.t:' + styleparseTypes[item.featureType] + '|';
      }
      if (item.elementType) {
        if (!styleparseElements[item.elementType]) {
          // console.log('style element transcription unkown:'+styles[i].elementType);
        }
        ret += 's.e:' + styleparseElements[item.elementType] + '|';
      }
      if (item.stylers) {
        for (const item1 of item.stylers) {
          const keys = [];
          const cstyler = item1
          for (const k in cstyler) {
            if (k === 'color') {
              if (cstyler[k].length === 7)
                cstyler[k] = '#ff' + cstyler[k].slice(1);
              else if (cstyler[k].length !== 9)
                Logger.error('malformed color:' + cstyler[k]);
            }
            ret += styleparseStylers[k] + ':' + cstyler[k] + '|';
          }
        }
      }
      ret = ret.slice(0, ret.length - 1);
      ret += ','
    }
    return encodeURIComponent(ret.slice(0, ret.length - 1));
  }

  getDarkStyle() {
    return [
      {
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#242f3e"
          }
        ]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#746855"
          }
        ]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#242f3e"
          }
        ]
      },
      {
        "featureType": "administrative.locality",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#d59563"
          }
        ]
      },
      {
        "featureType": "poi",
        "elementType": "labels",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#d59563"
          }
        ]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#263c3f"
          }
        ]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#6b9a76"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#38414e"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#212a37"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#9ca5b3"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#746855"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#1f2835"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#f3d19c"
          }
        ]
      },
      {
        "featureType": "transit",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#2f3948"
          }
        ]
      },
      {
        "featureType": "transit.station",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#d59563"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#17263c"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#515c6d"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#17263c"
          }
        ]
      }
    ]
  }

  getLightStyle() {
    return [
      {
        "featureType": "poi",
        "elementType": "labels",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      }
    ]
  }
}
